import { Controller } from "@hotwired/stimulus"

// Plantafel Filter Panel Controller
// Handles the filter dropdown UI including category switching, search, and selection management
export default class extends Controller {
  static targets = [
    "panel",
    "form",
    "searchInput",
    "categoryButton",
    "categoryContent",
    "filterItem",
    "allSitesCheckbox",
    "allBauleiterCheckbox",
    "allTasksCheckbox",
    "allTeamsCheckbox",
    "allTradesCheckbox",
    "allEmployeeTradesCheckbox",
    "allEmployeeTypesCheckbox",
    "allTaskTypesCheckbox",
    "siteCheckbox",
    "bauleiterCheckbox",
    "taskCheckbox",
    "teamCheckbox",
    "tradeCheckbox",
    "employeeTradeCheckbox",
    "employeeTypeCheckbox",
    "taskTypeCheckbox",
    "sectionCheckbox",
    "siteFilterRadio",
  ]

  static SECTION_VISIBILITY_KEY = "plantafel_section_states"

  connect() {
    // Close panel when clicking outside
    this.boundClickOutside = this.clickOutside.bind(this)
    document.addEventListener("click", this.boundClickOutside)

    // Track current category
    this.currentCategory = "sites"

    // Load and apply section visibility state
    this.loadSectionVisibilityState()
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
  }

  toggle(event) {
    event.stopPropagation()
    this.panelTarget.classList.toggle("hidden")

    // Reset search when opening
    if (!this.panelTarget.classList.contains("hidden")) {
      this.searchInputTarget.value = ""
      this.showAllItems()
    }
  }

  close() {
    this.panelTarget.classList.add("hidden")
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  selectCategory(event) {
    const category = event.currentTarget.dataset.category
    this.currentCategory = category

    // Update button styles
    this.categoryButtonTargets.forEach((button) => {
      if (button.dataset.category === category) {
        button.classList.add("bg-gray-100", "text-gray-900", "font-medium")
        button.classList.remove("text-gray-600")
      } else {
        button.classList.remove("bg-gray-100", "text-gray-900", "font-medium")
        button.classList.add("text-gray-600")
      }
    })

    // Show/hide category content
    this.categoryContentTargets.forEach((content) => {
      if (content.dataset.category === category) {
        content.classList.remove("hidden")
      } else {
        content.classList.add("hidden")
      }
    })

    // Clear search when switching categories
    this.searchInputTarget.value = ""
    this.showAllItems()
  }

  search(event) {
    const query = event.target.value.toLowerCase().trim()

    // Get visible category content
    const visibleContent = this.categoryContentTargets.find(
      (content) => content.dataset.category === this.currentCategory,
    )

    if (!visibleContent) return

    // Filter items in the visible category
    const items = visibleContent.querySelectorAll('[data-plantafel-filter-target="filterItem"]')
    items.forEach((item) => {
      const searchText = item.dataset.searchText || ""
      if (query === "" || searchText.includes(query)) {
        item.classList.remove("hidden")
      } else {
        item.classList.add("hidden")
      }
    })
  }

  showAllItems() {
    this.filterItemTargets.forEach((item) => {
      item.classList.remove("hidden")
    })
  }

  toggleAll(event) {
    const checkbox = event.target
    const isChecked = checkbox.checked

    if (isChecked) {
      // When "All" is checked, uncheck all individual items (meaning no filter = show all)
      this.uncheckIndividualItems()
    }
  }

  updateSelection(event) {
    const checkbox = event.target
    const category = this.getCategoryForCheckbox(checkbox)

    // If an individual item is checked, uncheck the "All" checkbox
    if (checkbox.checked) {
      this.uncheckAllCheckbox(category)
    }

    // If no individual items are checked, check the "All" checkbox
    this.updateAllCheckboxState(category)
  }

  getCategoryForCheckbox(checkbox) {
    if (this.hasSiteCheckboxTarget && this.siteCheckboxTargets.includes(checkbox)) {
      return "sites"
    }
    if (this.hasBauleiterCheckboxTarget && this.bauleiterCheckboxTargets.includes(checkbox)) {
      return "bauleiter"
    }
    if (this.hasTaskCheckboxTarget && this.taskCheckboxTargets.includes(checkbox)) {
      return "tasks"
    }
    if (this.hasTeamCheckboxTarget && this.teamCheckboxTargets.includes(checkbox)) {
      return "teams"
    }
    if (this.hasTradeCheckboxTarget && this.tradeCheckboxTargets.includes(checkbox)) {
      return "trades"
    }
    if (
      this.hasEmployeeTradeCheckboxTarget &&
      this.employeeTradeCheckboxTargets.includes(checkbox)
    ) {
      return "employee_trades"
    }
    if (this.hasEmployeeTypeCheckboxTarget && this.employeeTypeCheckboxTargets.includes(checkbox)) {
      return "employee_types"
    }
    if (this.hasTaskTypeCheckboxTarget && this.taskTypeCheckboxTargets.includes(checkbox)) {
      return "task_types"
    }
    return null
  }

  uncheckAllCheckbox(category) {
    switch (category) {
      case "sites":
        if (this.hasAllSitesCheckboxTarget) this.allSitesCheckboxTarget.checked = false
        break
      case "bauleiter":
        if (this.hasAllBauleiterCheckboxTarget) this.allBauleiterCheckboxTarget.checked = false
        break
      case "tasks":
        if (this.hasAllTasksCheckboxTarget) this.allTasksCheckboxTarget.checked = false
        break
      case "teams":
        if (this.hasAllTeamsCheckboxTarget) this.allTeamsCheckboxTarget.checked = false
        break
      case "trades":
        if (this.hasAllTradesCheckboxTarget) this.allTradesCheckboxTarget.checked = false
        break
      case "employee_trades":
        if (this.hasAllEmployeeTradesCheckboxTarget) {
          this.allEmployeeTradesCheckboxTarget.checked = false
        }
        break
      case "employee_types":
        if (this.hasAllEmployeeTypesCheckboxTarget) {
          this.allEmployeeTypesCheckboxTarget.checked = false
        }
        break
      case "task_types":
        if (this.hasAllTaskTypesCheckboxTarget) this.allTaskTypesCheckboxTarget.checked = false
        break
    }
  }

  updateAllCheckboxState(category) {
    let checkboxes, allCheckbox

    switch (category) {
      case "sites":
        checkboxes = this.hasSiteCheckboxTarget ? this.siteCheckboxTargets : []
        allCheckbox = this.hasAllSitesCheckboxTarget ? this.allSitesCheckboxTarget : null
        break
      case "bauleiter":
        checkboxes = this.hasBauleiterCheckboxTarget ? this.bauleiterCheckboxTargets : []
        allCheckbox = this.hasAllBauleiterCheckboxTarget ? this.allBauleiterCheckboxTarget : null
        break
      case "tasks":
        checkboxes = this.hasTaskCheckboxTarget ? this.taskCheckboxTargets : []
        allCheckbox = this.hasAllTasksCheckboxTarget ? this.allTasksCheckboxTarget : null
        break
      case "teams":
        checkboxes = this.hasTeamCheckboxTarget ? this.teamCheckboxTargets : []
        allCheckbox = this.hasAllTeamsCheckboxTarget ? this.allTeamsCheckboxTarget : null
        break
      case "trades":
        checkboxes = this.hasTradeCheckboxTarget ? this.tradeCheckboxTargets : []
        allCheckbox = this.hasAllTradesCheckboxTarget ? this.allTradesCheckboxTarget : null
        break
      case "employee_trades":
        checkboxes = this.hasEmployeeTradeCheckboxTarget ? this.employeeTradeCheckboxTargets : []
        allCheckbox = this.hasAllEmployeeTradesCheckboxTarget
          ? this.allEmployeeTradesCheckboxTarget
          : null
        break
      case "employee_types":
        checkboxes = this.hasEmployeeTypeCheckboxTarget ? this.employeeTypeCheckboxTargets : []
        allCheckbox = this.hasAllEmployeeTypesCheckboxTarget
          ? this.allEmployeeTypesCheckboxTarget
          : null
        break
      case "task_types":
        checkboxes = this.hasTaskTypeCheckboxTarget ? this.taskTypeCheckboxTargets : []
        allCheckbox = this.hasAllTaskTypesCheckboxTarget ? this.allTaskTypesCheckboxTarget : null
        break
      default:
        return
    }

    if (!allCheckbox) return

    // If no individual checkboxes are checked, check "All"
    const anyChecked = checkboxes.some((cb) => cb.checked)
    allCheckbox.checked = !anyChecked
  }

  uncheckIndividualItems() {
    // Uncheck all individual checkboxes in the current category
    switch (this.currentCategory) {
      case "sites":
        if (this.hasSiteCheckboxTarget) {
          this.siteCheckboxTargets.forEach((cb) => (cb.checked = false))
        }
        break
      case "bauleiter":
        if (this.hasBauleiterCheckboxTarget) {
          this.bauleiterCheckboxTargets.forEach((cb) => (cb.checked = false))
        }
        break
      case "tasks":
        if (this.hasTaskCheckboxTarget) {
          this.taskCheckboxTargets.forEach((cb) => (cb.checked = false))
        }
        break
      case "teams":
        if (this.hasTeamCheckboxTarget) {
          this.teamCheckboxTargets.forEach((cb) => (cb.checked = false))
        }
        break
      case "trades":
        if (this.hasTradeCheckboxTarget) {
          this.tradeCheckboxTargets.forEach((cb) => (cb.checked = false))
        }
        break
      case "employee_trades":
        if (this.hasEmployeeTradeCheckboxTarget) {
          this.employeeTradeCheckboxTargets.forEach((cb) => (cb.checked = false))
        }
        break
      case "employee_types":
        if (this.hasEmployeeTypeCheckboxTarget) {
          this.employeeTypeCheckboxTargets.forEach((cb) => (cb.checked = false))
        }
        break
      case "task_types":
        if (this.hasTaskTypeCheckboxTarget) {
          this.taskTypeCheckboxTargets.forEach((cb) => (cb.checked = false))
        }
        break
    }
  }

  clearAll() {
    // Uncheck all individual checkboxes
    if (this.hasSiteCheckboxTarget) {
      this.siteCheckboxTargets.forEach((cb) => (cb.checked = false))
    }
    if (this.hasBauleiterCheckboxTarget) {
      this.bauleiterCheckboxTargets.forEach((cb) => (cb.checked = false))
    }
    if (this.hasTaskCheckboxTarget) {
      this.taskCheckboxTargets.forEach((cb) => (cb.checked = false))
    }
    if (this.hasTeamCheckboxTarget) {
      this.teamCheckboxTargets.forEach((cb) => (cb.checked = false))
    }
    if (this.hasTradeCheckboxTarget) {
      this.tradeCheckboxTargets.forEach((cb) => (cb.checked = false))
    }
    if (this.hasEmployeeTradeCheckboxTarget) {
      this.employeeTradeCheckboxTargets.forEach((cb) => (cb.checked = false))
    }
    if (this.hasEmployeeTypeCheckboxTarget) {
      this.employeeTypeCheckboxTargets.forEach((cb) => (cb.checked = false))
    }
    if (this.hasTaskTypeCheckboxTarget) {
      this.taskTypeCheckboxTargets.forEach((cb) => (cb.checked = false))
    }

    // Check all "All" checkboxes
    if (this.hasAllSitesCheckboxTarget) this.allSitesCheckboxTarget.checked = true
    if (this.hasAllBauleiterCheckboxTarget) this.allBauleiterCheckboxTarget.checked = true
    if (this.hasAllTasksCheckboxTarget) this.allTasksCheckboxTarget.checked = true
    if (this.hasAllTeamsCheckboxTarget) this.allTeamsCheckboxTarget.checked = true
    if (this.hasAllTradesCheckboxTarget) this.allTradesCheckboxTarget.checked = true
    if (this.hasAllEmployeeTradesCheckboxTarget) this.allEmployeeTradesCheckboxTarget.checked = true
    if (this.hasAllEmployeeTypesCheckboxTarget) this.allEmployeeTypesCheckboxTarget.checked = true
    if (this.hasAllTaskTypesCheckboxTarget) this.allTaskTypesCheckboxTarget.checked = true

    // Reset the site visibility radio back to "all"
    if (this.hasSiteFilterRadioTarget) {
      this.siteFilterRadioTargets.forEach((radio) => (radio.checked = radio.value === "all"))
    }

    // Submit the form to apply the cleared filters
    if (this.hasFormTarget) {
      this.formTarget.submit()
    }
  }

  // Section Visibility Methods

  // Load section visibility state from localStorage and apply to checkboxes
  loadSectionVisibilityState() {
    const state = this.getSectionVisibilityState()

    if (this.hasSectionCheckboxTarget) {
      this.sectionCheckboxTargets.forEach((checkbox) => {
        const section = checkbox.dataset.section
        if (section) {
          // Default to true (visible) if not set in state
          checkbox.checked = state[section] !== false
        }
      })
    }

    // Dispatch initial state event so grid controller can apply it
    // Deferred to next animation frame to ensure all Stimulus controllers have connected
    // and registered their event listeners before the event fires
    requestAnimationFrame(() => {
      window.dispatchEvent(
        new CustomEvent("plantafel:section-visibility-loaded", {
          detail: { state },
        }),
      )
    })
  }

  // Get section visibility state from localStorage
  getSectionVisibilityState() {
    try {
      const saved = localStorage.getItem(this.constructor.SECTION_VISIBILITY_KEY)
      if (saved) {
        return JSON.parse(saved)
      }
    } catch (_e) {
      // Ignore storage errors
    }
    // Default: all sections visible
    return {
      appointments: true,
      sickNotes: true,
      leaveRequests: true,
    }
  }

  // Save section visibility state to localStorage
  saveSectionVisibilityState(state) {
    try {
      localStorage.setItem(this.constructor.SECTION_VISIBILITY_KEY, JSON.stringify(state))
    } catch (_e) {
      // Ignore storage errors
    }
  }

  // Handle section visibility toggle
  toggleSectionVisibility(event) {
    const checkbox = event.target
    const section = checkbox.dataset.section
    const visible = checkbox.checked

    // Get current state and update
    const state = this.getSectionVisibilityState()
    state[section] = visible
    this.saveSectionVisibilityState(state)

    // Dispatch event for grid controller to handle
    window.dispatchEvent(
      new CustomEvent("plantafel:section-visibility-changed", {
        detail: { section, visible, state },
      }),
    )
  }
}
